/*
 * AN45° — desktop header navigation behaviour.
 * ---------------------------------------------------------------------------
 * Dawn renders a nav item that has children as <details><summary>, so a parent
 * like "Products" could only be CLICKED to toggle its panel and could never be
 * followed to its own page. Owner's request (2026-08-05) — the shop-standard
 * behaviour instead:
 *   - HOVER the parent  -> its mega menu opens
 *   - CLICK the parent  -> go to the page the menu item points at
 *
 * The destination comes from `data-href` on the <summary>, which
 * snippets/header-mega-menu.liquid fills from `link.url`. So "Products" goes to
 * /collections/all because that is what the menu says in Shopify admin — repoint
 * it there and this follows. Nothing is hardcoded here.
 *
 * ⚠️ Scoped to FINE pointers. On a touch screen there is no hover, so the first
 * tap MUST still open the submenu or the child links become unreachable. Dawn
 * hides .header__inline-menu below 990px in favour of the drawer, but a touch
 * laptop is still possible — hence a pointer test rather than a width test.
 *
 * ⚠️ Keyboard behaviour is deliberately untouched: Enter/Space on the summary
 * still toggles the disclosure, which is how a keyboard user reaches the
 * children. Do not hijack keydown here.
 */
(function () {
  'use strict';
  if (typeof document === 'undefined') return;

  // Grace period covering the diagonal mouse travel from the summary down into
  // the panel; without it the menu snaps shut mid-move.
  var CLOSE_DELAY = 140;

  function isFinePointer() {
    return !!(window.matchMedia && window.matchMedia('(pointer: fine)').matches);
  }

  function wire(details) {
    var summary = details.querySelector('summary');
    if (!summary || details.dataset.an45Nav === '1') return;
    details.dataset.an45Nav = '1'; // idempotent: the header can re-render

    var timer = null;

    function open() {
      if (timer) { clearTimeout(timer); timer = null; }
      if (!details.hasAttribute('open')) {
        // Setting the attribute fires `toggle`, which Dawn's HeaderMenu already
        // listens for (it pins the sticky header open) — so we get that for free.
        details.setAttribute('open', '');
        summary.setAttribute('aria-expanded', 'true');
      }
    }

    function closeSoon() {
      if (timer) clearTimeout(timer);
      timer = setTimeout(function () {
        details.removeAttribute('open');
        summary.setAttribute('aria-expanded', 'false');
        timer = null;
      }, CLOSE_DELAY);
    }

    details.addEventListener('mouseenter', function () { if (isFinePointer()) open(); });
    details.addEventListener('mouseleave', function () { if (isFinePointer()) closeSoon(); });

    var href = summary.getAttribute('data-href');
    if (!href || href === '#') return; // heading-only item: leave it as a toggle

    summary.addEventListener('click', function (e) {
      if (!isFinePointer()) return; // touch: let the first tap reveal the submenu
      e.preventDefault();           // stop <details> swallowing the click as a toggle
      if (e.metaKey || e.ctrlKey || e.shiftKey) {
        window.open(href, '_blank', 'noopener');
      } else {
        window.location.href = href;
      }
    });
  }

  function init() {
    document.querySelectorAll('.header__inline-menu details').forEach(wire);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
